/**
 * InfoPopover Component
 * A reusable popover component for displaying informational content
 * with a dark theme, icon, title, and close button
 */

import {AutoAwesome, Close} from '@mui/icons-material'
import {Box, IconButton, Popover, PopoverOrigin, Typography} from '@mui/material'
import React from 'react'

/**
 * Props for the InfoPopover component
 */
export interface InfoPopoverProps {
  /** Whether the popover is open */
  open: boolean
  /** The anchor element for the popover positioning */
  anchorEl: HTMLElement | SVGElement | null
  /** Callback when the popover should close */
  onClose: () => void
  /** The title text to display in the header */
  title: string
  /** The content text to display in the body */
  content: string
  /** Optional custom icon (defaults to AutoAwesome) */
  icon?: React.ReactNode
  /** Optional custom max width (defaults to 360px) */
  maxWidth?: string
  /** Optional custom anchor origin (defaults to bottom center) */
  anchorOrigin?: PopoverOrigin
  /** Optional custom transform origin (defaults to top center) */
  transformOrigin?: PopoverOrigin
  /** Test ID for testing purposes */
  testId?: string
}

/**
 * InfoPopover - A styled popover for displaying informational messages
 * 
 * Features:
 * - Dark theme with arrow pointer
 * - Customizable icon and title
 * - Close button
 * - Configurable positioning
 * - Memoized for performance
 * 
 * @example
 * ```tsx
 * const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
 * const [content, setContent] = useState('')
 * 
 * <InfoPopover
 *   open={Boolean(anchorEl)}
 *   anchorEl={anchorEl}
 *   onClose={() => setAnchorEl(null)}
 *   title="Changed mix characteristics"
 *   content={content}
 * />
 * ```
 */
export const InfoPopover: React.FC<InfoPopoverProps> = React.memo(
  ({
    open,
    anchorEl,
    onClose,
    title,
    content,
    icon,
    maxWidth = '360px',
    anchorOrigin = {
      vertical: 'bottom' as const,
      horizontal: 'center' as const
    },
    transformOrigin = {
      vertical: 'top' as const,
      horizontal: 'center' as const
    },
    testId = 'info-popover'
  }) => {
    // Default icon if none provided
    const displayIcon = icon ?? (
      <AutoAwesome
        sx={{
          fontSize: '16px',
          color: '#ffffff'
        }}
      />
    )

    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        data-test-id={testId}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#1f262e',
              borderRadius: '8px',
              padding: 0,
              maxWidth,
              marginTop: '8px',
              overflow: 'visible',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-8px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderBottom: '8px solid #1f262e'
              }
            }
          }
        }}
      >
        {/* Header with icon, title, and close button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            paddingX: '16px',
            paddingY: 0
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flex: 1,
              paddingY: '8px'
            }}
          >
            {displayIcon}
            <Typography
              sx={{
                color: '#ffffff',
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '24px'
              }}
            >
              {title}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            disableRipple
            aria-label="Close"
            data-test-id={`${testId}-close-button`}
            sx={{
              color: (theme) => theme.palette.common.white,
              boxShadow: 'none',
              width: '24px',
              height: '24px',
              borderRadius: 'none',
              marginRight: '-8px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <Close
              fontSize="small"
              sx={{
                width: '16px',
                height: '16px'
              }}
            />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            paddingBottom: '12px',
            paddingTop: 0,
            paddingX: '16px'
          }}
        >
          <Typography
            sx={{
              color: '#ffffff',
              fontFamily: 'Inter',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px'
            }}
          >
            {content}
          </Typography>
        </Box>
      </Popover>
    )
  }
)

InfoPopover.displayName = 'InfoPopover'
